/**
 * PRP-WEB-2 W2.6: what the Problems strip lists. Detection is web-2-core's `planProblems` (W1.5:
 * overlaps over 1 in, rooms touching nothing, rooms with no door, estimated sizes); this adds a
 * closet subroom that does not touch its main room, and the two fixes ("Nudge apart", "Move
 * inside <room>"), each one undo step through the page.
 */
import {
  bbox, endRoomDrag, findRoom, planProblems as corePlanProblems, polysOverlap, sharedMap, snapIn, type Plan, type Room,
} from '@sketch-core';
import { groupOf, type Groups } from '../groups/groups';

export type Problem =
  | { kind: 'overlap'; rid: string; other: string; depthFt: number; text: string }
  | { kind: 'estimated' | 'no-door' | 'touches-nothing'; rid: string; text: string }
  | { kind: 'closet-outside'; rid: string; host: string; text: string };

/** "Nudge apart" is offered below this overlap; past it the rooms are in the wrong place. */
export const NUDGE_MAX_FT = 2;

function overlapDepth(a: Room, b: Room): { depth: number; ax: 'x' | 'y' } | null {
  if (!polysOverlap(a.corners, b.corners)) return null;
  const [ax0, ay0, ax1, ay1] = bbox(a.corners), [bx0, by0, bx1, by1] = bbox(b.corners);
  const ox = Math.min(ax1, bx1) - Math.max(ax0, bx0), oy = Math.min(ay1, by1) - Math.max(ay0, by0);
  if (ox <= 0 || oy <= 0) return null;
  return ox <= oy ? { depth: ox, ax: 'x' } : { depth: oy, ax: 'y' };
}

const isCloset = (r: Room) => /closet|\bclo\b|\bwic\b|linen|pantry/i.test(r.name);
const area2 = (r: Room) => Math.abs(r.corners.reduce((s, a, i) => { const b = r.corners[(i + 1) % r.corners.length]; return s + a.x * b.y - b.x * a.y; }, 0));

export function planProblems(plan: Plan, groups: Groups): Problem[] {
  const out: Problem[] = [];
  for (const p of corePlanProblems(plan, groups)) {
    if (p.kind === 'overlap' && p.rooms.length >= 2) {
      const [a, b] = p.rooms.map((id) => findRoom(plan, id)!);
      if (!a || !b) continue;
      const small = area2(a) <= area2(b) ? a : b, big = small === a ? b : a;
      out.push({ kind: 'overlap', rid: small.id, other: big.id, depthFt: (p.overlapIn ?? 0) / 12, text: p.message });
    } else if (p.rooms[0]) {
      const kind = p.kind === 'no_door' ? 'no-door' : p.kind === 'touches_nothing' ? 'touches-nothing' : 'estimated';
      out.push({ kind, rid: p.rooms[0], text: p.message } as Problem);
    }
  }
  const M = sharedMap(plan);
  for (const r of plan.rooms) {
    const g = groupOf(groups, r.id);
    if (!g || g.role !== 'sub' || !isCloset(r)) continue;
    const host = findRoom(plan, g.group.main_room_id);
    if (!host) continue;
    const touches = [...(M.get(r.id)?.values() ?? [])].some((ivs) => ivs.some((iv) => iv.orid === host.id));
    if (!touches && !overlapDepth(r, host)) out.push({ kind: 'closet-outside', rid: r.id, host: host.id, text: `${r.name} is outside ${host.name}` });
  }
  return out;
}

function shift(r: Room, dx: number, dy: number) { r.corners.forEach((p) => { p.x = snapIn(p.x + dx); p.y = snapIn(p.y + dy); }); }

/** "Nudge apart": move the smaller room out by the overlap plus an interior wall (one undo step). */
export function nudgeApart(plan: Plan, rid: string, other: string): { ok: true } | { ok: false; message: string } {
  const a = findRoom(plan, rid), b = findRoom(plan, other);
  const o = a && b ? overlapDepth(a, b) : null;
  if (!a || !b || !o) return { ok: false, message: 'Those rooms no longer overlap.' };
  if (o.depth >= NUDGE_MAX_FT) return { ok: false, message: 'They overlap too much to nudge. Drag the room where it belongs.' };
  const [ax0, ay0, ax1, ay1] = bbox(a.corners), [bx0, by0, bx1, by1] = bbox(b.corners);
  const wall = plan.defaults.interiorWallIn / 12;
  const d = o.depth + wall;
  if (o.ax === 'x') shift(a, (ax0 + ax1) / 2 < (bx0 + bx1) / 2 ? -d : d, 0);
  else shift(a, 0, (ay0 + ay1) / 2 < (by0 + by1) / 2 ? -d : d);
  endRoomDrag(plan);
  return { ok: true };
}

/** "Move inside <room>": the closet goes into the main room's nearest corner, inside its walls. */
export function moveInside(plan: Plan, rid: string, host: string): { ok: true } | { ok: false; message: string } {
  const c = findRoom(plan, rid), h = findRoom(plan, host);
  if (!c || !h) return { ok: false, message: 'That room is gone.' };
  const [cx0, cy0, cx1, cy1] = bbox(c.corners), [hx0, hy0, hx1, hy1] = bbox(h.corners);
  if (cx1 - cx0 > hx1 - hx0 || cy1 - cy0 > hy1 - hy0) return { ok: false, message: `${c.name} is bigger than ${h.name}.` };
  const mx = (cx0 + cx1) / 2, my = (cy0 + cy1) / 2;
  const tx = Math.abs(mx - hx0) < Math.abs(mx - hx1) ? hx0 - cx0 : hx1 - cx1;
  const ty = Math.abs(my - hy0) < Math.abs(my - hy1) ? hy0 - cy0 : hy1 - cy1;
  shift(c, tx, ty);
  endRoomDrag(plan);
  return { ok: true };
}
